import { useEffect, useRef } from 'react';

let backCloseSeq = 0;

/**
 * Mobile back-gesture integration for overlays (sheets, modals, drawer).
 *
 * While `active`, pushes a history entry so the browser/OS back gesture
 * closes the overlay (calls onClose) instead of navigating away from the
 * app — the "swipe back exits to the previous website" bug.
 *
 * If the overlay is closed by its own UI (X button, backdrop tap), the
 * pushed entry is consumed with history.back() so the stack stays clean.
 *
 * Callers gate with isMobile: desktop keeps existing behavior untouched —
 * pass `active = isOpen && isMobile`.
 */
export function useBackClose(active, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return undefined;

    const id = `back-close-${++backCloseSeq}`;
    let closedByPop = false;
    window.history.pushState({ __backClose: id }, '');

    const onPop = () => {
      closedByPop = true;
      onCloseRef.current?.();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed programmatically (X / backdrop / selection): consume our
      // sentinel entry so the next back gesture doesn't need two presses.
      if (!closedByPop && window.history.state && window.history.state.__backClose === id) {
        window.history.back();
      }
    };
  }, [active]);
}

export default useBackClose;
